import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useManuallyUpdate from './useManuallyUpdate';
import { RootState } from '../redux/root';
import { setSearchToken } from '../redux/slices/filterSlice';

export enum TablePageRequestStatus {
  Request = 'REQUEST',
  Success = 'SUCCESS',
  Failure = 'FAILURE',
}

export enum TablePageRequestName {
  Create = 'CREATE',
  Update = 'UPDATE',
  Delete = 'DELETE',
}

const defaultEndpointPlaceholder = data =>
  new Promise(resolve => resolve(data));

const useTablePage = <T extends object>({
  createEndpoint = defaultEndpointPlaceholder,
  updateEndpoint = defaultEndpointPlaceholder,
  deleteEndpoint = defaultEndpointPlaceholder,
  modalCreateTitle = 'Add',
  modalUpdateTitle = 'Update',
  onRequestStatusChange = () => {},
}: useTablePageSettings): useTablePageReturnType<T> => {
  const dispatch = useDispatch();
  const search = useSelector((state: RootState) => state.filters.searchToken);
  const setSearch = token => {
   dispatch(setSearchToken(token));
  };
  const [modalSuccess, setModalSuccess] = React.useState<
    boolean
  >(false);
  const [modalError, setModalError] = React.useState<
    boolean
  >(false);

  const [isModalOpen, setIsModalOpen] = React.useState<
    boolean
  >(false);

  const [isApiSuccess, setIsApiSuccess] = React.useState<boolean>(false);
  const [successResponse, setSuccessResponse] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState<T>(
    null,
  );

  const [
    modalErrorTitle,
    setModalErrorTitle,
  ] = React.useState<string>('');

  const [modalTitle, setModalTitle] = React.useState<
    string
  >('');

  const [values, setValues] = React.useState(null);
  const [errors, setErrors] = React.useState(null);
  const [
    innerModalErrors,
    setInnerModalErrors,
  ] = React.useState(null);

  const [
    tableManuallyUpdate,
    updateTable,
  ] = useManuallyUpdate();

  const createOrUpdate = fields => {
    const id =
      fields instanceof FormData
        ? fields.get('id')
        : fields.id;
    const endpoint = id ? updateEndpoint : createEndpoint;

    const requestStatusName = id
      ? TablePageRequestName.Update
      : TablePageRequestName.Create;

    onRequestStatusChange({
      name: requestStatusName,
      status: TablePageRequestStatus.Request,
    });

    return endpoint(fields)
      .then(res => {
        setSuccessResponse(res);
        updateTable();

        // EA-1195: If inspector was cleared due to trade mismatch,
        // show warning message. Modal closes and table refreshes — when the
        // user re-opens the inspection, inspector will be cleared.
        const responseData = res?.data;
        if (responseData?.inspectorCleared || responseData?.inspector_cleared) {
          setModalErrorTitle(responseData?.inspectorWarning || responseData?.inspector_warning || 'Inspector was cleared due to trade qualification change. Please re-open the inspection to assign a new inspector.');
          setModalError(true);
        }

        setIsModalOpen(false);
        if (fields.userRole !== "3" && !(responseData?.inspectorCleared || responseData?.inspector_cleared))
        { setModalSuccess(true); }
        setValues(null);
        setErrors(null);
        setInnerModalErrors(null);
        if (!(responseData?.inspectorCleared || responseData?.inspector_cleared)) {
          setModalErrorTitle('');
        }
        setIsApiSuccess(true);
        onRequestStatusChange({
          name: requestStatusName,
          status: TablePageRequestStatus.Success,
        });
      })
      .catch(error => {
        const { data, status } = error.response;

        // Payload too large: nginx answers with an HTML 413 body, which would
        // otherwise surface as a raw technical error. The per-flow limits differ
        // (multipart vs base64-in-JSON), so keep this backstop copy generic —
        // the upload controls themselves name the concrete number.
        if (status === 413) {
          setModalError(true);
          setModalErrorTitle(
            'The file size is too large. Please upload a file below the allowed file size limit.',
          );
          return;
        }

        if (status > 500) {
          setModalError(true);
          return;
        }

        if (
          status <= 500 &&
          !data &&
          Object.keys(data).length === 0
        ) {
          setModalError(true);
          setModalErrorTitle(
            'Something went wrong. Please, contact support',
          );
          return;
        }

        // EA-1372: QBO-down hard-block on send_invoice. This error is surfaced
        // by the GLOBAL error modal (ModalErrorRequest), which renders the clean
        // "Invoicing is currently unavailable…" message. Do NOT also set the
        // per-table modal error here, or the local FeesSendModal ModalInfo would
        // pop a duplicate red alert on top of the Send form. Swallow it and let
        // the global modal own the messaging.
        if (data?.error === 'qbo_unavailable') {
          return;
        }

        if (
          status <= 500 &&
          data.nonFieldErrors &&
          typeof data.nonFieldErrors !== 'string'
        ) {
          setModalError(true);
          setModalErrorTitle(
            data.nonFieldErrors.join(', '),
          );
          return;
        }

        if (
          status <= 500 &&
          data.nonFieldErrors &&
          typeof data.nonFieldErrors === 'string'
        ) {
          setModalError(true);
          setModalErrorTitle(data.nonFieldErrors);
          return;
        }

        if (
          status === 400 &&
          data &&
          data?.nonFieldErrors
        ) {
          setModalError(true);
          setModalErrorTitle(data?.nonFieldErrors);
          return;
        }

        setErrors(data);
        setInnerModalErrors(data);

        onRequestStatusChange({
          name: requestStatusName,
          status: TablePageRequestStatus.Failure,
        });
        throw error;
      });
  };

  const handleAdd = () => {
    setModalTitle(modalCreateTitle);
    setIsModalOpen(true);
    setIsApiSuccess(false);
  };

  const handleEdit = data => {
    setValues(data);
    setModalTitle(data.isDeleted ? '' : modalUpdateTitle);
    setIsModalOpen(true);
  };

  const handleDelete = item => setSelectedItem(item);

  const deleteById = id => {
    onRequestStatusChange({
      name: TablePageRequestName.Delete,
      status: TablePageRequestStatus.Request,
    });

    return deleteEndpoint(id)
      .then(() => {
        updateTable();
        setModalSuccess(true);
        setSelectedItem(null);

        onRequestStatusChange({
          name: TablePageRequestName.Delete,
          status: TablePageRequestStatus.Success,
        });
      })
      .catch(() => {
        // Disable for now
        // setModalError(true);
        // setModalErrorTitle(e.response?.data?.detail);
        setSelectedItem(null);

        onRequestStatusChange({
          name: TablePageRequestName.Delete,
          status: TablePageRequestStatus.Failure,
        });
      });
  };

  const handleCloseModal = () => {
    setModalTitle('');
    setValues(null);
    setErrors(null);
    setInnerModalErrors(null);
    setIsModalOpen(false);
  };

  const handleCloseModalInfo = () => {
    setModalSuccess(false);
    setModalError(false);
    setIsApiSuccess(false);
  };

  return {
    search,
    setSearch,
    modalError,
    modalSuccess,
    showErrorModal: setModalError,
    showSuccessModal: setModalSuccess,
    isModalOpen,
    openModal: setIsModalOpen,
    selectedItem,
    setSelectedItem,
    modalTitle,
    setModalTitle,
    values,
    setValues,
    errors,
    innerModalErrors,
    setErrors,
    tableManuallyUpdate,
    updateTable,
    createOrUpdate,
    deleteById,
    handleAdd,
    isApiSuccess,
    successResponse,
    handleEdit,
    handleDelete,
    handleCloseModal,
    handleCloseModalInfo,
    modalErrorTitle,
  };
};

interface useTablePageSettings {
  createEndpoint?: (data: any) => Promise<any>;
  updateEndpoint?: (data: any) => Promise<any>;
  deleteEndpoint?: (id: number) => Promise<any>;
  modalCreateTitle?: string;
  modalUpdateTitle?: string;
  onRequestStatusChange?: ({
    status: TablePageRequestStatus,
    name: TablePageRequestName,
  }) => void;
}

interface useTablePageReturnType<T extends object> {
  search: string;
  setSearch: (search: string) => void;
  modalError: boolean;
  modalSuccess: boolean;
  showErrorModal: (status: boolean) => void;
  showSuccessModal: (status: boolean) => void;
  isModalOpen: boolean;
  openModal: (status: boolean) => void;
  selectedItem: T;
  setSelectedItem: (item: T) => void;
  modalTitle: string;
  setModalTitle: (title: string) => void;
  values: any;
  setValues: (values: any) => any;
  innerModalErrors: any;
  errors: any;
  setErrors: (values: any) => any;
  tableManuallyUpdate: number;
  updateTable: () => void;
  createOrUpdate: (fields: any) => Promise<any>;
  deleteById: (id: number) => void;
  handleAdd: () => void;
  handleEdit: (data: T) => void;
  handleDelete: (data: T) => void;
  handleCloseModal: () => void;
  handleCloseModalInfo: () => void;
  modalErrorTitle: string;
  isPaidManuallySubmit: boolean;
  setIsPaidManuallySubmit: (data: boolean) => void;
}

export default useTablePage;
